// ImportChecklist - every input the overview can use, as a slot: received,
// partial (with what is covered), or missing, plus what it unlocks and
// whether this server can decode it (guide sections 14 and 15). It is the
// answer to "why does this zone say not in received drawings".
import React, { useEffect, useState } from "react";
import { Button, Skeleton } from "antd";
import { url, tokenRef } from "api";

// What each check found (utils/glance/inputs resultsFor): a mark AND a word,
// so the state does not ride on colour alone.
const CHECK_MARK = { pass: "✓", warn: "!", fail: "✗", "not-run": "–" };
const CHECK_WORD = { pass: "Passed", warn: "Look at this", fail: "Failed", "not-run": "Not run" };

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const STATUS = { received: "Received", partial: "Partial", missing: "Missing" };

export default function ImportChecklist({ projectId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || data) return;
    fetch(`${url}/projects/${projectId}/glance/inputs`, { headers: auth() })
      .then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`); return b; })
      .then(setData).catch((e) => setError(e.message));
  }, [open, data, projectId]);

  const counts = data ? data.slots.reduce((n, s) => ({ ...n, [s.status]: (n[s.status] || 0) + 1 }), {}) : null;
  return (
    <section>
      <h2>What the overview is built from</h2>
      <p className="g-note">
        Every input the overview can use, what it unlocks, and whether it has arrived. Upload what is missing in Document Control or Schedule Import.
      </p>
      {!open && <Button size="small" onClick={() => setOpen(true)}>Show the import checklist</Button>}
      {open && !data && !error && <Skeleton active paragraph={{ rows: 4 }} />}
      {error && <div className="g-empty">{error}</div>}
      {data && (
        <>
          <p className="g-status">
            {`${counts.received || 0} received, ${counts.partial || 0} partial, ${counts.missing || 0} missing.`}
          </p>
          {data.duplicates.length > 0 && (
            <div className="g-callout">
              <b>Same page filed more than once.</b> {data.duplicates.slice(0, 3).map((d) => d.sheets.join(" = ")).join("; ")}
              {data.duplicates.length > 3 ? `, and ${data.duplicates.length - 3} more` : ""}.
            </div>
          )}
          <div className="g-tablewrap">
            <table>
              <thead>
                <tr><th>Input</th><th>Status</th><th>Priority</th><th>Unlocks</th><th>Checks on import</th><th>Decoder on this server</th></tr>
              </thead>
              <tbody>
                {data.slots.map((s) => (
                  <tr key={s.id}>
                    <td>{s.label}<div className="g-tsub">{s.formats}</div></td>
                    <td>
                      <span className={`g-pill g-st-${s.status}`}>{STATUS[s.status]}</span>
                      {s.detail && <div className="g-tsub">{s.detail}</div>}
                      {s.gaps.map((g) => <div key={g} className="g-tsub">{g}</div>)}
                      {s.status !== "missing" && !s.onPage && s.id !== "revit" && s.id !== "tracking" && s.id !== "construction-schedule" && (
                        <div className="g-tsub">Not on the overview yet</div>
                      )}
                    </td>
                    <td>{s.priority}</td>
                    <td>{s.unlocks}</td>
                    <td>
                      {s.checks}
                      {(s.results || []).map((r, i) => (
                        <div key={i} className={`g-check g-check-${r.status}`}>
                          <span className="g-check-mark" aria-hidden="true">{CHECK_MARK[r.status] || "·"}</span>
                          <span className="g-sr">{CHECK_WORD[r.status] || ""}: </span>{r.text}
                        </div>
                      ))}
                    </td>
                    <td>
                      {!s.decoders.length ? "Built in" : s.decoders.map((d) => (
                        <div key={d.id} className={d.available ? "" : "g-tsub"}>
                          {d.label}: {d.available ? "ready" : `not installed (${d.missing.join(", ")})`}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
